/**
 * Утилита для расчета уверенности в анализе
 * Реализует систему выбора алгоритма в зависимости от количества аргументов
 * Базовый уровень уверенности: 50% (равенство аргументов)
 */

/**
 * Основная функция для расчета уверенности с автоматическим выбором алгоритма
 * @param totalPlusScore - общий балл положительных аргументов
 * @param totalMinusScore - общий балл отрицательных аргументов
 * @param totalArgumentsCount - общее количество аргументов (опционально)
 * @returns уверенность в процентах (0-100)
 */
export function calculateConfidence(totalPlusScore: number, totalMinusScore: number, totalArgumentsCount?: number): number {
  // Если нет аргументов, возвращаем минимальную уверенность
  if (totalPlusScore === 0 && totalMinusScore === 0) {
    return 0;
  }

  // Если не передан totalArgumentsCount, вычисляем его из баллов
  const argumentsCount = totalArgumentsCount || Math.round((totalPlusScore + totalMinusScore) / 2);

  // Выбираем алгоритм в зависимости от количества аргументов
  if (argumentsCount <= 3) {
    // Мало аргументов - используем логарифмический (консервативный)
    return calculateLogarithmicConfidence(totalPlusScore, totalMinusScore);
  } else if (argumentsCount <= 8) {
    // Среднее количество - используем основной нелинейный
    return calculateNonlinearConfidence(totalPlusScore, totalMinusScore);
  } else {
    // Много аргументов - используем экспоненциальный (агрессивный)
    return calculateExponentialConfidence(totalPlusScore, totalMinusScore);
  }
}

/**
 * Основной нелинейный алгоритм (для среднего количества аргументов)
 * @param totalPlusScore - общий балл положительных аргументов
 * @param totalMinusScore - общий балл отрицательных аргументов
 * @returns уверенность в процентах (0-100)
 */
function calculateNonlinearConfidence(totalPlusScore: number, totalMinusScore: number): number {
  // Если нет аргументов, возвращаем минимальную уверенность
  if (totalPlusScore === 0 && totalMinusScore === 0) {
    return 0;
  }

  // Вычисляем общий балл
  const totalScore = totalPlusScore + totalMinusScore;
  
  // Вычисляем разность (абсолютное значение)
  const difference = Math.abs(totalPlusScore - totalMinusScore);
  
  // Базовый процент уверенности (равенство аргументов = 50%)
  const baseConfidence = 50;
  
  // Максимальная уверенность
  const maxConfidence = 95;
  
  // Коэффициент для нелинейного роста
  const nonlinearFactor = 2.5;
  
  // Рассчитываем нелинейную уверенность
  const ratio = difference / totalScore;
  const nonlinearConfidence = baseConfidence + (maxConfidence - baseConfidence) * Math.pow(ratio, 1 / nonlinearFactor);
  
  // Округляем до целого числа
  return Math.round(Math.min(maxConfidence, Math.max(baseConfidence, nonlinearConfidence)));
}

/**
 * Рассчитывает уверенность с использованием экспоненциальной функции
 * Используется для большого количества аргументов (агрессивный рост)
 * @param totalPlusScore - общий балл положительных аргументов
 * @param totalMinusScore - общий балл отрицательных аргументов
 * @returns уверенность в процентах (0-100)
 */
function calculateExponentialConfidence(totalPlusScore: number, totalMinusScore: number): number {
  // Если нет аргументов, возвращаем минимальную уверенность
  if (totalPlusScore === 0 && totalMinusScore === 0) {
    return 0;
  }

  // Вычисляем общий балл
  const totalScore = totalPlusScore + totalMinusScore;
  
  // Вычисляем разность (абсолютное значение)
  const difference = Math.abs(totalPlusScore - totalMinusScore);
  
  // Базовый процент уверенности (равенство аргументов = 50%)
  const baseConfidence = 50;
  
  // Максимальная уверенность
  const maxConfidence = 95;
  
  // Рассчитываем экспоненциальную уверенность
  // Используем экспоненциальную функцию для очень быстрого роста при больших различиях
  const ratio = difference / totalScore;
  const exponentialConfidence = baseConfidence + (maxConfidence - baseConfidence) * (1 - Math.exp(-2.5 * ratio));
  
  // Округляем до целого числа
  return Math.round(Math.min(maxConfidence, Math.max(baseConfidence, exponentialConfidence)));
}

/**
 * Рассчитывает уверенность с использованием логарифмической функции
 * Используется для малого количества аргументов (консервативный рост)
 * @param totalPlusScore - общий балл положительных аргументов
 * @param totalMinusScore - общий балл отрицательных аргументов
 * @returns уверенность в процентах (0-100)
 */
function calculateLogarithmicConfidence(totalPlusScore: number, totalMinusScore: number): number {
  // Если нет аргументов, возвращаем минимальную уверенность
  if (totalPlusScore === 0 && totalMinusScore === 0) {
    return 0;
  }

  // Вычисляем общий балл
  const totalScore = totalPlusScore + totalMinusScore;
  
  // Вычисляем разность (абсолютное значение)
  const difference = Math.abs(totalPlusScore - totalMinusScore);
  
  // Базовый процент уверенности (равенство аргументов = 50%)
  const baseConfidence = 50;
  
  // Максимальная уверенность
  const maxConfidence = 95;
  
  // Рассчитываем логарифмическую уверенность
  // Используем логарифмическую функцию для плавного роста
  const ratio = difference / totalScore;
  const logarithmicConfidence = baseConfidence + (maxConfidence - baseConfidence) * Math.log(1 + 1.5 * ratio) / Math.log(2.5);
  
  // Округляем до целого числа
  return Math.round(Math.min(maxConfidence, Math.max(baseConfidence, logarithmicConfidence)));
}

